import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { getWishlist } from '@theme/wishlist-button';

/**
 * Populates this section with the visitor's wishlisted products by re-rendering it
 * against Shopify's search route, filtered down to the wishlisted product ids.
 *
 * This looks the product ids up directly in the store's search index instead of
 * rendering every product in the store and hiding the ones that don't match, so it
 * keeps working no matter how large the catalog is.
 *
 * @extends {Component}
 */
class WishlistGrid extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.render);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.render);
  }

  render = async () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const wishlist = getWishlist();
    // "id:0" never matches a real product - it guarantees search.performed is true
    // with zero results, so the empty state can be driven by the same Liquid check
    // (search.performed and products.size == 0) whether the wishlist is empty or
    // every wishlisted product has since been deleted.
    const query = wishlist.length > 0 ? wishlist.map((id) => `id:${id}`).join(' OR ') : 'id:0';

    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');

    await sectionRenderer.renderSection(sectionId, { cache: false, url });

    // The freshly fetched markup is rendered server-side, so it has no idea which
    // products are wishlisted - re-sync every button against localStorage now that
    // the morph has settled, otherwise they'd all render as inactive.
    for (const button of this.querySelectorAll('wishlist-button')) {
      if (typeof (/** @type {any} */ (button).sync) === 'function') {
        /** @type {any} */ (button).sync();
      }
    }
  };
}

if (!customElements.get('wishlist-grid-component')) {
  customElements.define('wishlist-grid-component', WishlistGrid);
}
